import { create } from "zustand";

/**
 * An optional single action rendered inside the toast (e.g. "Undo"). The
 * handler owns its own feedback — the toast dismisses as soon as it is clicked.
 */
export interface ToastAction {
  label: string;
  onClick: () => void | Promise<void>;
}

export interface ToastData {
  type: "success" | "error" | "warning" | "info";
  message: string;
  id?: string;
  duration?: number;
  action?: ToastAction;
}

/**
 * Toasts carrying an action stay up longer than the 4s default: the user has to
 * notice the toast, read it, and travel to the button before it disappears.
 * 12s is the same order of magnitude as the undo windows in Gmail/Drive and is
 * still short enough that a stale toast is not sitting on screen.
 */
export const TOAST_ACTION_DURATION = 12000;

interface GlobalState {
  error: string | null;
  setError: (error: string | null) => void;
  clearError: () => void;

  toast: ToastData | null;
  setToast: (toast: ToastData | null) => void;
  clearToast: () => void;

  // `action` is optional and additive: existing two-argument calls are
  // unaffected. Passing an action (without an explicit duration) switches the
  // toast to the longer TOAST_ACTION_DURATION so the button is clickable.
  showSuccess: (
    message: string,
    duration?: number,
    action?: ToastAction,
  ) => void;
  showError: (message: string, duration?: number, action?: ToastAction) => void;
  showWarning: (
    message: string,
    duration?: number,
    action?: ToastAction,
  ) => void;
  showInfo: (message: string, duration?: number, action?: ToastAction) => void;
}

const resolveDuration = (
  duration: number | undefined,
  action: ToastAction | undefined,
  fallback: number,
) => duration ?? (action ? TOAST_ACTION_DURATION : fallback);

const useGlobalStore = create<GlobalState>((set, get) => ({
  error: null,
  setError: (error: string | null) => {
    set({ error });
    if (error) {
      get().showError(error);
    }
  },
  clearError: () => set({ error: null }),

  toast: null,
  setToast: (toast: ToastData | null) => set({ toast }),
  clearToast: () => set({ toast: null }),

  showSuccess: (message: string, duration?: number, action?: ToastAction) =>
    set({
      toast: {
        type: "success",
        message,
        id: Date.now().toString(),
        duration: resolveDuration(duration, action, 4000),
        action,
      },
    }),

  showError: (message: string, duration?: number, action?: ToastAction) =>
    set({
      toast: {
        type: "error",
        message,
        id: Date.now().toString(),
        duration: resolveDuration(duration, action, 6000),
        action,
      },
    }),

  showWarning: (message: string, duration?: number, action?: ToastAction) =>
    set({
      toast: {
        type: "warning",
        message,
        id: Date.now().toString(),
        duration: resolveDuration(duration, action, 5000),
        action,
      },
    }),

  showInfo: (message: string, duration?: number, action?: ToastAction) =>
    set({
      toast: {
        type: "info",
        message,
        id: Date.now().toString(),
        duration: resolveDuration(duration, action, 4000),
        action,
      },
    }),
}));

export default useGlobalStore;
